import type { Locale } from "next/router";

import dayjs from "dayjs";

import { formatMoney } from "../../../config/price-format";
import type { PricePlace } from "../../../config/price-format";
import { Currency } from "@/Apollo/schema";
import { formatDateWithLocale } from "@/common/utils/intl.utils";
import { toContentLocale } from "@/i18n/config";

/** Day, abbreviated month, year, no comma: `27 aug 2026` / `27 Aug 2026`. */
export const ACCOUNT_DATE_FORMAT = "D MMM YYYY";

/**
 * German puts a period after the day and abbreviates months the CLDR way
 * (`3. Nov. 2026`, `3. März 2026`). dayjs's German has neither (`3 Nov 2026`),
 * so German dates come from `Intl` (WHO-6641).
 */
const GERMAN_ACCOUNT_DATE = new Intl.DateTimeFormat("de-DE", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

export const formatAccountDate = (value: string, locale?: Locale | string) => {
  if (locale && toContentLocale(locale) === "de") {
    return GERMAN_ACCOUNT_DATE.format(dayjs(value).toDate());
  }

  return formatDateWithLocale(
    value,
    ACCOUNT_DATE_FORMAT,
    (locale ?? "nl") as Locale
  );
};

/**
 * Deadline under the Actie nodig pill: day + abbreviated month, no year
 * (`voor 10 sep`).
 */
export const formatAccountDeadline = (
  value: string,
  locale?: Locale | string
) =>
  dayjs(value)
    // dayjs has a file per content language: `en-us` reads `en` (WHO-5766).
    .locale(locale ? toContentLocale(locale) : "nl")
    .format("D MMM");

/**
 * Account lists and details. A thin wrapper on the site's one formatter
 * (config/price-format.js), so an ad's price reads the same in My ads and on
 * its detail page. Unlike `formatPrice` it shows zero (`€0`). Payouts, My
 * sales and every payout breakdown pass `place: "accounting"` (`€ 925,00`).
 */
export const formatAccountMoney = (
  amount: number | null | undefined,
  options: { locale?: Locale | string; currency?: Currency; place?: PricePlace }
): string =>
  formatMoney(amount, {
    locale: options.locale,
    currency: options.currency,
    place: options.place,
  });
